import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import BaseURL from '../../../assets/contants/BaseURL';
import useLiveValue from '../useLiveValue';
import useMeterField from '../useMeterField';

const POSITION_CELL = {
    'top-left': [1, 1], 'top-center': [1, 2], 'top-right': [1, 3],
    'middle-left': [2, 1], center: [2, 2], 'middle-right': [2, 3],
    'bottom-left': [3, 1], 'bottom-center': [3, 2], 'bottom-right': [3, 3],
};

function layoutFields(fields) {
    const cells = fields.map((f) => POSITION_CELL[f.position] || POSITION_CELL.center);
    const rows = [...new Set(cells.map((c) => c[0]))].sort((a, b) => a - b);
    const cols = [...new Set(cells.map((c) => c[1]))].sort((a, b) => a - b);
    const areas = cells.map(([r, c]) => ({
        gridRow: rows.indexOf(r) + 1,
        gridColumn: cols.indexOf(c) + 1,
    }));
    return { rows: rows.length, cols: cols.length, areas };
}

const MetricCustomField = ({ field, meterId, isPreview, precision, textColor, showTrend, gridArea }) => {
    const report = useMeterField(field.title, { meterId, skip: isPreview });
    const value = report.field ? (report.latest ?? 0) : 0;
    const display = Number(value).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: precision });

    const points = report.points || [];
    const previous = points.length > 1 ? points[points.length - 2].value : null;
    const delta = previous ? Number((((value - previous) / previous) * 100).toFixed(1)) : 0;
    const isUp = delta >= 0;

    const [justUpdated, setJustUpdated] = useState(false);
    const prevValueRef = useRef(value);
    useEffect(() => {
        if (!report.field || prevValueRef.current === value) return undefined;
        prevValueRef.current = value;
        setJustUpdated(true);
        const t = setTimeout(() => setJustUpdated(false), 900);
        return () => clearTimeout(t);
    }, [value, report.field]);

    return (
        <div
            className={`metric-widget__field${justUpdated ? ' metric-widget__field--pulse' : ''}`}
            style={gridArea}
            title={report.field ? `${field.title}: ${value}${field.unit ? ` ${field.unit}` : ''}` : `${field.title} — not connected`}
        >
            {field.title && <div className="metric-widget__caption">{field.title}</div>}
            <div className="metric-widget__value-row">
                <span className="metric-widget__value" style={{ color: textColor }}>{display}</span>
                {field.unit && <span className="metric-widget__unit">{field.unit}</span>}
            </div>
            {showTrend && report.field && previous !== null && (
                <div className={`metric-widget__trend${isUp ? ' metric-widget__trend--up' : ' metric-widget__trend--down'}`}>
                    <i className={`fas fa-arrow-${isUp ? 'up' : 'down'}`}></i> {Math.abs(delta)}%
                </div>
            )}
        </div>
    );
};

const MetricWidget = ({ config }) => {
    const { dataSource = {}, settings = {}, variant = 'big-number', title } = config;
    const baseValue = Number(settings.value ?? dataSource.staticValue ?? 0) || 0;
    const [fetchedValue, setFetchedValue] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const isPreview = typeof config.id === 'string' && config.id.startsWith('preview-');
    const report = useMeterField(title, { meterId: settings.meterId, skip: Boolean(dataSource.endpoint) || isPreview });
    const liveValue = useLiveValue(baseValue, {
        id: config.id, endpoint: dataSource.endpoint || (report.field ? 'report' : ''), min: 0, max: Math.max(baseValue * 2, 1), volatility: 0.03,
    });

    useEffect(() => {
        if (!dataSource.endpoint) return;
        let cancelled = false;
        setLoading(true);
        const token = sessionStorage.getItem('token');

        axios.get(`${BaseURL}${dataSource.endpoint}`, {
            headers: { Authorization: `Token ${token}` },
        })
            .then((res) => {
                if (cancelled) return;
                const data = res.data;
                setFetchedValue(dataSource.field ? data?.[dataSource.field] : data);
                setError(false);
            })
            .catch((err) => {
                console.error('MetricWidget: failed to fetch', err);
                if (!cancelled) setError(true);
            })
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [dataSource.endpoint, dataSource.field]);

    const value = dataSource.endpoint
        ? (fetchedValue ?? baseValue)
        : (report.field ? (report.latest ?? 0) : liveValue);
    const precision = settings.precision ?? 2;
    const numericValue = Number(value);
    const display = Number.isFinite(numericValue)
        ? numericValue.toLocaleString(undefined, { maximumFractionDigits: precision })
        : String(value ?? '--');
    const accent = settings.color || '#4318FF';
    const textColor = settings.textColor || accent;
    const unit = settings.unit || '';
    const tooltip = `${display}${unit ? ` ${unit}` : ''}`;
    const delta = baseValue ? Number((((value - baseValue) / baseValue) * 100).toFixed(1)) : 0;
    const isUp = delta >= 0;

    if (loading) {
        return <div className="metric-widget" title={tooltip}><span className="metric-widget__loading">Loading…</span></div>;
    }
    if (error) {
        return <div className="metric-widget" title={tooltip}><span className="metric-widget__error">Failed to load</span></div>;
    }

    switch (variant) {
        case 'icon-leading':
            return (
                <div className="metric-widget metric-widget--icon-leading" title={tooltip}>
                    <div className="metric-widget__icon" style={{ backgroundColor: `${accent}1A`, color: accent }}>
                        <i className="fas fa-bolt"></i>
                    </div>
                    <div className="metric-widget__stack">
                        <div className="metric-widget__value" style={{ color: textColor }}>{display}</div>
                        {unit && <div className="metric-widget__unit">{unit}</div>}
                    </div>
                </div>
            );

        case 'with-trend':
            return (
                <div className="metric-widget" title={tooltip}>
                    <div className="metric-widget__value" style={{ color: textColor }}>{display}</div>
                    {unit && <div className="metric-widget__unit">{unit}</div>}
                    <div className={`metric-widget__trend${isUp ? ' metric-widget__trend--up' : ' metric-widget__trend--down'}`}>
                        <i className={`fas fa-arrow-${isUp ? 'up' : 'down'}`}></i> {Math.abs(delta)}%
                    </div>
                </div>
            );

        case 'gradient-card':
            return (
                <div
                    className="metric-widget metric-widget--gradient"
                    title={tooltip}
                    style={{ background: `linear-gradient(135deg, ${accent}, ${accent}99)` }}
                >
                    <div className="metric-widget__value">{display}</div>
                    {unit && <div className="metric-widget__unit">{unit}</div>}
                </div>
            );

        case 'pill-badge':
            return (
                <div className="metric-widget" title={tooltip}>
                    <div className="metric-widget__pill" style={{ backgroundColor: `${accent}1A`, color: accent }}>
                        {display}{unit && <span className="metric-widget__unit"> {unit}</span>}
                    </div>
                </div>
            );

        case 'bordered-box':
            return (
                <div className="metric-widget" title={tooltip}>
                    <div className="metric-widget__box" style={{ borderColor: accent }}>
                        <div className="metric-widget__value" style={{ color: textColor }}>{display}</div>
                        {unit && <div className="metric-widget__unit">{unit}</div>}
                    </div>
                </div>
            );

        case 'minimal':
            return (
                <div className="metric-widget" title={tooltip}>
                    <div className="metric-widget__value metric-widget__value--minimal" style={{ color: textColor }}>{display}</div>
                </div>
            );

        case 'label-top':
            return (
                <div className="metric-widget" title={tooltip}>
                    <div className="metric-widget__caption">{unit || config.title}</div>
                    <div className="metric-widget__value" style={{ color: textColor }}>{display}</div>
                </div>
            );

        case 'split-unit':
            return (
                <div className="metric-widget" title={tooltip}>
                    <div className="metric-widget__inline">
                        <span className="metric-widget__value" style={{ color: textColor }}>{display}</span>
                        {unit && <span className="metric-widget__unit metric-widget__unit--inline">{unit}</span>}
                    </div>
                </div>
            );

        case 'ring-accent':
            return (
                <div className="metric-widget" title={tooltip}>
                    <div className="metric-widget__ring" style={{ borderColor: `${accent}33`, borderTopColor: accent, borderRightColor: accent }}>
                        <div className="metric-widget__value" style={{ color: textColor }}>{display}</div>
                        {unit && <div className="metric-widget__unit">{unit}</div>}
                    </div>
                </div>
            );

        case 'custom': {
            const showIcon = Boolean(settings.customShowIcon);
            const showTrend = Boolean(settings.customShowTrend);
            const iconClass = settings.customIconClass || 'fas fa-bolt';
            const fields = Array.isArray(settings.customFields) && settings.customFields.length
                ? settings.customFields
                : [{ title: '', unit: '', position: 'center' }];
            const { rows, cols, areas } = layoutFields(fields);

            return (
                <div
                    className="metric-widget metric-widget--fields"
                    style={{ gridTemplateRows: `repeat(${rows}, 1fr)`, gridTemplateColumns: `repeat(${cols}, 1fr)` }}
                >
                    {showIcon && (
                        <div className="metric-widget__icon metric-widget__icon--floating" style={{ backgroundColor: `${accent}1A`, color: accent }}>
                            <i className={iconClass}></i>
                        </div>
                    )}
                    {fields.map((field, i) => (
                        <MetricCustomField
                            key={`${field.title}-${i}`}
                            field={field}
                            meterId={settings.meterId}
                            isPreview={isPreview}
                            precision={precision}
                            textColor={textColor}
                            showTrend={showTrend}
                            gridArea={areas[i]}
                        />
                    ))}
                </div>
            );
        }

        case 'big-number':
        default:
            return (
                <div className="metric-widget" title={tooltip}>
                    <div className="metric-widget__value" style={{ color: textColor }}>{display}</div>
                    {unit && <div className="metric-widget__unit">{unit}</div>}
                </div>
            );
    }
};

export default MetricWidget;
